import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { roundToUnit } from "./estadistica";

const SHEET = "MT Precios";
const TABLE = "ProductosMT";

const PRODUCTOS = [
  "Tornillo hexagonal", "Taquete plástico", "Brocha 2\"", "Cinta de aislar", "Lija de agua",
  "Pegamento epóxico", "Clavo de acero", "Silicón transparente", "Guantes de nitrilo", "Foco LED 9 W",
  "Contacto doble", "Cable calibre 12 (m)", "Llave de paso", "Codo PVC 1/2\"", "Thinner (L)",
  "Pintura vinílica (L)", "Rodillo 9\"", "Flexómetro 5 m", "Martillo de uña", "Desarmador plano",
  "Pinzas de presión", "Candado 40 mm", "Bisagra 3\"", "Chapa de pomo", "Escalera 5 peldaños",
] as const;

const rnd = seededRandom(6161);
// Producto, Costo (pesos enteros), Margen, Unidades pedidas, Unidades por caja
const ROWS = PRODUCTOS.map((producto) => [
  producto,
  rnd.int(6, 900),
  rnd.pick([0.25, 0.3, 0.35, 0.4, 0.45, 0.5]),
  rnd.int(10, 600),
  rnd.pick([6, 10, 12, 24, 25, 50]),
] as [string, number, number, number, number]);

type Row = (typeof ROWS)[number];
// Everything in integer cents so the rounding is exact.
const precioCents = (r: Row) => Math.round(r[1] * 100 * (1 + r[2]));
const ivaCents = (r: Row) => roundToUnit(precioCents(r) * 16, 100) / 100;
const totalCents = (r: Row) => precioCents(r) + ivaCents(r);
const final99Cents = (r: Row) => Math.ceil(totalCents(r) / 100) * 100 - 1;

export const spec: FuncionProjectSpec = {
  projectId: "funciones-redondeo",
  sheets: [
    {
      name: SHEET,
      title: "Precios, IVA y cajas",
      subtitle: "Lista de precios de una ferretería — redondea como lo hace un sistema de facturación",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Producto", "Costo", "Margen", "Unidades pedidas", "Unidades por caja"],
          rows: ROWS,
          answerColumns: [
            "Precio sin IVA",
            "IVA",
            "Precio .99",
            "Cajas a pedir",
            "Cajas completas",
            "Piezas sueltas",
            "Precio caja",
            "Precio mayoreo",
          ],
          formats: {
            Costo: "$#,##0.00",
            Margen: "0%",
            "Precio sin IVA": "$#,##0.00",
            IVA: "$#,##0.00",
            "Precio .99": "$#,##0.00",
            "Precio caja": "$#,##0",
            "Precio mayoreo": "$#,##0",
          },
        },
      ],
    },
  ],
  checks: {
    1: { kind: "column", sheet: SHEET, table: TABLE, column: "Precio sin IVA", fns: ["ROUND"], expected: ROWS.map((r) => precioCents(r) / 100) },
    2: { kind: "column", sheet: SHEET, table: TABLE, column: "IVA", fns: ["ROUND"], expected: ROWS.map((r) => ivaCents(r) / 100) },
    3: { kind: "column", sheet: SHEET, table: TABLE, column: "Precio .99", fns: ["ROUNDUP"], expected: ROWS.map((r) => final99Cents(r) / 100) },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Cajas a pedir", fns: ["ROUNDUP"],
      expected: ROWS.map((r) => Math.ceil(r[3] / r[4])),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Cajas completas", fns: ["INT"],
      expected: ROWS.map((r) => Math.floor(r[3] / r[4])),
    },
    6: { kind: "column", sheet: SHEET, table: TABLE, column: "Piezas sueltas", fns: ["MOD"], expected: ROWS.map((r) => r[3] % r[4]) },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Precio caja", fns: ["ROUND"],
      expected: ROWS.map((r) => roundToUnit(final99Cents(r) * r[4], 1000) / 100),
    },
    8: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Precio mayoreo", fns: ["MROUND"],
      expected: ROWS.map((r) => roundToUnit(final99Cents(r), 500) / 100),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-redondeo",
  title: "REDONDEAR y familia: Precios, IVA y cajas",
  subtitle: "Redondear bien es dinero",
  description:
    "Llevas la lista de precios de una ferretería. Tienes que calcular precios con margen, el IVA a centavos, precios que terminen en .99, cuántas cajas pedir para surtir un pedido y precios de caja y de mayoreo redondeados.\n\nCambiar el formato solo cambia cómo se ve el número; las funciones de redondeo cambian el número de verdad. Si facturas sin redondear, los centavos se acumulan y los totales no cuadran.",
  topics: [
    "REDONDEAR a centavos y a decenas (decimales negativos)",
    "REDONDEAR.MAS: siempre hacia arriba",
    "ENTERO y RESIDUO: cajas completas y piezas sueltas",
    "REDOND.MULT: al múltiplo más cercano",
    "Precios psicológicos terminados en .99",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Precio con margen",
      description:
        "En la hoja <strong>MT Precios</strong>, columna <strong>Precio sin IVA</strong>: Costo × (1 + Margen), redondeado a <strong>2 decimales</strong> (centavos).",
      solution: [`Selecciona ${F("F5")}.`, `Escribe ${F("=REDONDEAR([@Costo]*(1+[@Margen]),2)")}.`],
      expectedResult: "Precio a centavos",
      finalFormula: "=REDONDEAR([@Costo]*(1+[@Margen]),2)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: IVA a centavos",
      description: "En la columna <strong>IVA</strong>: el <strong>16%</strong> del Precio sin IVA, redondeado a <strong>2 decimales</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "El SAT y cualquier sistema de facturación redondean el impuesto a centavos en cada partida.",
        `Escribe ${F("=REDONDEAR([@[Precio sin IVA]]*16%,2)")}.`,
      ],
      expectedResult: "IVA a centavos",
      finalFormula: "=REDONDEAR([@[Precio sin IVA]]*16%,2)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Precio terminado en .99",
      description: conditions("En la columna <strong>Precio .99</strong>: precio de venta al público terminado en 99 centavos:", [
        "Suma Precio sin IVA + IVA",
        "Redondea <strong>hacia arriba</strong> al peso entero",
        "Réstale <strong>1 centavo</strong>",
      ]),
      solution: [
        `Selecciona ${F("H5")}.`,
        "REDONDEAR.MAS siempre sube, aunque el decimal sea pequeño: 127.10 → 128.",
        `Escribe ${F("=REDONDEAR.MAS([@[Precio sin IVA]]+[@IVA],0)-1%")}. ${F("1%")} es 0.01 (un centavo) y se escribe igual tanto si tu Excel usa punto decimal como si usa coma.`,
      ],
      expectedResult: "127.99",
      finalFormula: "=REDONDEAR.MAS([@[Precio sin IVA]]+[@IVA],0)-1%",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Cajas a pedir",
      description:
        "En la columna <strong>Cajas a pedir</strong>: cuántas cajas hay que pedir para cubrir las <strong>Unidades pedidas</strong>. Si faltan aunque sea unas piezas, se pide una caja más.",
      solution: [
        `Selecciona ${F("I5")}.`,
        "Divide unidades entre unidades por caja y redondea siempre hacia arriba: 130 ÷ 24 = 5.4 → 6 cajas.",
        `Escribe ${F("=REDONDEAR.MAS([@[Unidades pedidas]]/[@[Unidades por caja]],0)")}.`,
      ],
      expectedResult: "6 cajas para 130 piezas de 24 por caja",
      finalFormula: "=REDONDEAR.MAS([@[Unidades pedidas]]/[@[Unidades por caja]],0)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Cajas completas",
      description: "En la columna <strong>Cajas completas</strong>: cuántas cajas <strong>llenas</strong> se pueden armar con las Unidades pedidas (sin contar la incompleta).",
      solution: [
        `Selecciona ${F("J5")}.`,
        "ENTERO quita los decimales hacia abajo: 5.4 → 5.",
        `Escribe ${F("=ENTERO([@[Unidades pedidas]]/[@[Unidades por caja]])")}.`,
      ],
      expectedResult: "5 cajas llenas",
      finalFormula: "=ENTERO([@[Unidades pedidas]]/[@[Unidades por caja]])",
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Piezas sueltas",
      description: "En la columna <strong>Piezas sueltas</strong>: cuántas piezas quedan fuera de las cajas completas.",
      solution: [
        `Selecciona ${F("K5")}.`,
        "RESIDUO devuelve lo que sobra de una división: 130 entre 24 son 5 cajas y sobran 10.",
        `Escribe ${F("=RESIDUO([@[Unidades pedidas]],[@[Unidades por caja]])")}.`,
      ],
      expectedResult: "10",
      finalFormula: "=RESIDUO([@[Unidades pedidas]],[@[Unidades por caja]])",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Precio por caja a decenas",
      description:
        "En la columna <strong>Precio caja</strong>: Precio .99 × Unidades por caja, redondeado a la <strong>decena de pesos</strong> más cercana (3,071.76 → 3,070).",
      solution: [
        `Selecciona ${F("L5")}.`,
        `Con decimales negativos, REDONDEAR trabaja a la izquierda del punto: ${F("-1")} = decenas, ${F("-2")} = centenas.`,
        `Escribe ${F("=REDONDEAR([@[Precio .99]]*[@[Unidades por caja]],-1)")}.`,
      ],
      expectedResult: "3,070",
      finalFormula: "=REDONDEAR([@[Precio .99]]*[@[Unidades por caja]],-1)",
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Precio de mayoreo",
      description:
        "En la columna <strong>Precio mayoreo</strong>: el Precio .99 llevado al <strong>múltiplo de 5 pesos más cercano</strong> (127.99 → 130; 121.99 → 120).",
      solution: [
        `Selecciona ${F("M5")}.`,
        "REDOND.MULT(número, múltiplo) redondea al múltiplo más cercano, hacia arriba o hacia abajo.",
        `Escribe ${F("=REDOND.MULT([@[Precio .99]],5)")}.`,
      ],
      expectedResult: "130",
      finalFormula: "=REDOND.MULT([@[Precio .99]],5)",
    },
  ],
};
